<script lang="ts" setup>
  import { onMounted, ref, type Ref, onBeforeUpdate, onUpdated, onBeforeUnmount } from 'vue';
  import type { Photo } from '@storefront/resources';
  import type { NavButtons } from '@storefront/types';
  import Swiper from 'swiper';
  import { Navigation, Autoplay, Grid } from 'swiper/modules';
  import 'swiper/css';
  import 'swiper/css/navigation';
  import type { SwiperOptions } from 'swiper/types';

  type Category = {
    title: string | null,
    photo: Photo
  }

  interface Props {
    categories: Category[];
    parameters?: SwiperOptions | null | undefined;
    enableAutoplay?: boolean,
    timeAutoplay?: number | string,
    speed?: number | string,
  }

  const props = withDefaults(defineProps<Props>(), {
    parameters: () => ({}),
    enableAutoplay: false,
    timeAutoplay: 3000,
    speed: 400,
  });

  const htmlElement = ref() as Ref<HTMLElement>;
  const htmlNavigation = ref() as Ref<NavButtons>;
  const swiper = ref() as Ref<Swiper>;

  function initializeSwipers() {
    swiper.value = new Swiper(htmlElement.value, {
      modules: [Navigation, Autoplay, Grid],
      autoplay: props.enableAutoplay ? { delay: Number(props.timeAutoplay ?? 3000) } : false,
      speed: Number(props.speed ?? 400),
      slidesPerView: 1.5,
      breakpoints: {
        1: {
          slidesPerView: 2.5,
          spaceBetween: 10,
          slidesOffsetBefore: 10,
        },
        576: {
          slidesPerView: 3.5,
          spaceBetween: 10,
          slidesOffsetBefore: 10,
          slidesOffsetAfter: 10,
        },
        992: {
          slidesPerView: 4,
          spaceBetween: 16,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        },
        1200: {
          slidesPerView: 5,
          spaceBetween: 16,
          slidesOffsetBefore: 0,
        },
        1400: {
          slidesPerView: 6,
          spaceBetween: 16,
          slidesOffsetBefore: 0,
        },
      },
      ...props.parameters,
      navigation: {
        nextEl: htmlNavigation.value.next.htmlElement,
        prevEl: htmlNavigation.value.prev.htmlElement,
      },
    });
  }

  function destroySwipers() {
    swiper.value?.destroy();
  }

  onMounted(() => {
    if (props.categories.length > 0) {
      initializeSwipers();
    }
  });

  onBeforeUpdate(() => {
    destroySwipers();
  });

  onUpdated(() => {
    if (props.categories.length > 0) {
      initializeSwipers();
    }
  });

  onBeforeUnmount(() => {
    destroySwipers();
  });
</script>
<template>
  <div v-if="categories.length > 0" class="items-carousel categories-carousel">
    <div ref="htmlElement" class="swiper items-carousel__container categories-carousel__container">
      <div class="swiper-wrapper">
        <div v-for="category in categories" class="swiper-slide items-carousel__slide categories-carousel__article">
          <RouterLink class="categories-carousel__link" :href="$route('web.product.category', { category })">
            <div class="categories-carousel__image-wrapper">
              <MediaPicture :alt="category.title" :photo="category.photo" sizes="350x350 2x, 175x175" type="photo" />
            </div>
            <span v-if="category.title" class="categories-carousel__category-title">
              {{ category.title }}
            </span>
          </RouterLink>
        </div>
      </div>
    </div>
    <SfCarouselButtons ref="htmlNavigation" />
  </div>
</template>

<style lang="scss">
  :root {
    --categories-carousel-item-border: 1px solid var(--secondary-border);
    --categories-carousel-item-border-hover: var(--priamry-border-hover);
  }
</style>
<style lang="scss" scoped>
  .categories-carousel {

    &__image-wrapper {
      width: 100%;
    }

    &__category-title {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      word-break: break-word;
      text-decoration: underline;
      text-decoration-color: transparent;
      transition: text-decoration-color 0.2s ease-out;

      @media(hover: hover) {
        &:hover {
          text-decoration-color: var(--primary-color);
        }
      }
    }

    &__link {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
      padding: 1rem;
      flex-grow: 1;
      text-align: center;
      border: var(--categories-carousel-item-border);
      border-radius: var(--border-radius);
      transition-property: border-color;
      transition-timing-function: var(--transition-primary-timing-function);
      transition-duration: var(--transition-primary-duration);
      border-radius: var(--border-radius);
      overflow: hidden;

      &:focus-visible {
        outline: 2px solid;
        outline-offset: -4px;
      }

      :deep(img) {
        aspect-ratio: 1;
        object-fit: contain;
      }

      @media(hover: hover) {
        &:hover {
          border-color: var(--categories-carousel-item-border-hover);
        }
      }
    }

    @media(max-width: 768px) {
      &__category-title {
        font-size: 0.75rem;
      }
    }
  }
</style>

